import React, { useEffect, useMemo, useRef, useState } from "react";
import PromocionesModal from "./PromocionesModal.jsx";
import { listarNoLaborablesPromocion } from "../../../../services/promocionesService";
import { previsualizarCodigo } from "../../../../services/convocatoriaPlantillasService";
import CalendarioNoLaborableCalendarModal from "../../calendario/components/CalendarioNoLaborableCalendarModal.jsx";

const renderCicloOptions = (ciclos = [], loading) => {
  if (loading) {
    return [<option key="loading" value="">Cargando ciclos...</option>];
  }
  if (!ciclos.length) {
    return [<option key="empty" value="">No hay ciclos disponibles</option>];
  }
  return [
    <option key="placeholder" value="">Selecciona un ciclo</option>,
    ...ciclos.map((ciclo) => (
      <option key={ciclo.id} value={ciclo.id}>
        {ciclo.label || ciclo.nombre}
      </option>
    )),
  ];
};

const renderCursoOptions = (cursos = [], cursosLoading, cicloId) => {
  if (!cicloId) {
    return [
      <option key="no-ciclo" value="">
        Selecciona un ciclo primero
      </option>,
    ];
  }
  if (cursosLoading) {
    return [
      <option key="loading" value="">
        Cargando cursos...
      </option>,
    ];
  }
  if (!cursos.length) {
    return [
      <option key="empty" value="">
        No hay cursos en este ciclo
      </option>,
    ];
  }
  return [
    <option key="placeholder" value="">
      Selecciona un curso
    </option>,
    ...cursos.map((curso) => (
      <option key={curso.id} value={curso.id}>
        {curso.nombre}
      </option>
    )),
  ];
};

const formatHorarioLabel = (horario) => {
  const inicio = horario?.hora_inicio ? String(horario.hora_inicio).slice(0, 5) : "";
  const fin = horario?.hora_fin ? String(horario.hora_fin).slice(0, 5) : "";
  const rango = inicio && fin ? ` (${inicio}-${fin})` : "";
  const dias = buildDiasRango(horario?.dias_semana);
  return `${horario?.nombre || "Horario"}${rango}${dias ? ` ${dias}` : ""}`;
};

const renderHorarioOptions = (horarios = [], horariosLoading, cursoId) => {
  if (!cursoId) {
    return [
      <option key="no-curso" value="">
        Selecciona un curso primero
      </option>,
    ];
  }
  if (horariosLoading) {
    return [
      <option key="loading" value="">
        Cargando horarios...
      </option>,
    ];
  }
  if (!horarios.length) {
    return [
      <option key="empty" value="">
        No hay horarios disponibles
      </option>,
    ];
  }
  return [
    <option key="placeholder" value="">
      Selecciona un horario
    </option>,
    ...horarios.map((horario) => (
      <option key={horario.id} value={horario.id}>
        {formatHorarioLabel(horario)}
      </option>
    )),
  ];
};

const DIA_SHORT = {
  dom: "Dom",
  domingo: "Dom",
  lun: "Lun",
  lunes: "Lun",
  mar: "Mar",
  martes: "Mar",
  mie: "Mie",
  miercoles: "Mie",
  jue: "Jue",
  jueves: "Jue",
  vie: "Vie",
  viernes: "Vie",
  sab: "Sab",
  sabado: "Sab",
};

const formatDiaToken = (token) => {
  const normalized = normalizeDia(token);
  if (!normalized) return "";
  if (DIA_SHORT[normalized]) return DIA_SHORT[normalized];
  const short = normalized.slice(0, 3);
  return DIA_SHORT[short] || short;
};

const buildDiasRango = (diasSemanaRaw) => {
  if (!diasSemanaRaw) return "";
  const tokens = String(diasSemanaRaw)
    .split("-")
    .map((item) => item.trim())
    .filter(Boolean);
  if (!tokens.length) return "";
  const first = formatDiaToken(tokens[0]);
  const last = formatDiaToken(tokens[tokens.length - 1]);
  if (!first && !last) return "";
  if (first && last && first !== last) return `${first}-${last}`;
  return first || last;
};

const WEEK_LABELS = ["Lun", "Mar", "Mie", "Jue", "Vie", "Sab", "Dom"];
const MONTH_LABELS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const pad2 = (value) => String(value).padStart(2, "0");
const toDateKey = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
const toDisplayDate = (value) => {
  if (!value) return "-";
  const [year, month, day] = String(value).split("-").map((v) => Number(v));
  if (!year || !month || !day) return value;
  return `${pad2(day)}/${pad2(month)}/${year}`;
};

const parseInputDate = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).split("-").map((v) => Number(v));
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const normalizeDia = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const DIA_MAP = {
  dom: 0,
  domingo: 0,
  lun: 1,
  lunes: 1,
  mar: 2,
  martes: 2,
  mie: 3,
  miercoles: 3,
  jue: 4,
  jueves: 4,
  vie: 5,
  viernes: 5,
  sab: 6,
  sabado: 6,
};

const parseDiasSemanaSet = (value) => {
  const tokens = String(value || "")
    .split("-")
    .map((item) => normalizeDia(item))
    .filter(Boolean);
  const dias = tokens
    .map((token) => (DIA_MAP[token] !== undefined ? DIA_MAP[token] : DIA_MAP[token.slice(0, 3)]))
    .filter((day) => day !== undefined);
  return new Set(dias);
};

const buildCalendarWeeks = (year, month) => {
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const start = new Date(first.getFullYear(), first.getMonth(), first.getDate() - startOffset);
  const weeks = [];
  const cursor = new Date(start);

  for (let w = 0; w < 6; w += 1) {
    const week = [];
    for (let d = 0; d < 7; d += 1) {
      week.push({
        date: new Date(cursor),
        inMonth: cursor.getMonth() === month,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }

  return weeks;
};

// Días de calendario entre dos fechas "YYYY-MM-DD", ambas incluidas.
const contarDiasCalendario = (startKey, endKey) => {
  if (!startKey || !endKey) return null;
  const inicio = parseInputDate(startKey);
  const fin = parseInputDate(endKey);
  if (!inicio || !fin) return null;
  return Math.round((fin - inicio) / 86400000) + 1;
};

const PromocionCalendarioRange = ({
  startDate,
  endDate,
  onSelectDate,
  activeMonth,
  setActiveMonth,
  nonLaborables = [],
  allowedWeekdays,
  allowSelection = true,
  sessionDates = [],
  onClose,
  onOpenFeriados,
  loading = false,
}) => {
  const start = useMemo(() => parseInputDate(startDate), [startDate]);
  const end = useMemo(() => parseInputDate(endDate), [endDate]);
  const startKey = start ? toDateKey(start) : "";
  const endKey = end ? toDateKey(end) : "";
  const sessionList = useMemo(
    () =>
      (sessionDates || [])
        .map((item) => item?.fecha)
        .filter(Boolean)
        .sort(),
    [sessionDates]
  );
  const sessionSet = useMemo(() => new Set(sessionList), [sessionList]);
  const sessionStartKey = sessionList[0] || "";
  const sessionEndKey = sessionList.length ? sessionList[sessionList.length - 1] : "";
  // Prioriza lo que el usuario tiene puesto en el formulario (startKey/endKey)
  // sobre lo que ya se calculó de sesiones reales: si editó "Fecha de fin" a
  // mano para declarar un cierre oficial más adelante que la última clase
  // real, el calendario debe reflejar ESE valor en la etiqueta "Fin:" y en el
  // día marcado como cierre — si no, se ve el dato viejo aunque el input ya
  // se haya guardado con el nuevo. El coloreado de días con clase real (más
  // abajo, `inRange`) no depende de esto, así que nunca "pinta" un día sin
  // clase como si la hubiera.
  const rangeStart = startKey || sessionStartKey || "";
  const rangeEnd = endKey || sessionEndKey || "";
  const nonLaborableSet = useMemo(
    () => new Set((nonLaborables || []).map((item) => String(item))),
    [nonLaborables]
  );

  const weeks = useMemo(
    () => buildCalendarWeeks(activeMonth.getFullYear(), activeMonth.getMonth()),
    [activeMonth]
  );

  const isInRange = (key) =>
    rangeStart && rangeEnd && key >= rangeStart && key <= rangeEnd;

  const [yearInputValue, setYearInputValue] = useState(String(activeMonth.getFullYear()));
  const [showMonthMenu, setShowMonthMenu] = useState(false);
  const monthMenuRef = useRef(null);

  useEffect(() => {
    if (!showMonthMenu) return;
    const handleClickOutside = (event) => {
      if (monthMenuRef.current && !monthMenuRef.current.contains(event.target)) {
        setShowMonthMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMonthMenu]);

  const handlePrev = () => {
    const next = new Date(activeMonth.getFullYear(), activeMonth.getMonth() - 1, 1);
    setActiveMonth(next);
    setYearInputValue(String(next.getFullYear()));
  };

  const handleNext = () => {
    const next = new Date(activeMonth.getFullYear(), activeMonth.getMonth() + 1, 1);
    setActiveMonth(next);
    setYearInputValue(String(next.getFullYear()));
  };

  const handleMonthPick = (idx) => {
    setActiveMonth(new Date(activeMonth.getFullYear(), idx, 1));
    setShowMonthMenu(false);
  };

  const handleYearChange = (e) => {
    setYearInputValue(e.target.value);
  };

  const handleYearBlur = () => {
    const year = Number(yearInputValue);
    if (year >= 1900 && year <= 2100) {
      setActiveMonth(new Date(year, activeMonth.getMonth(), 1));
    } else {
      setYearInputValue(String(activeMonth.getFullYear()));
    }
  };

  const handleYearKeyDown = (e) => {
    if (e.key === "Enter") e.target.blur();
  };

  return (
    <div className={`promocion-calendario ${loading ? "is-loading" : ""}`}>
      <div className="promocion-calendario-header">
        <button type="button" className="btn btn-link p-0" onClick={handlePrev}>
          <i className="bi bi-chevron-left" />
        </button>
        <div>
          <div className="promocion-calendario-nav">
            <div className="promocion-calendario-month-dropdown" ref={monthMenuRef}>
              <button
                type="button"
                className="promocion-calendario-select"
                onClick={() => setShowMonthMenu((prev) => !prev)}
              >
                {MONTH_LABELS[activeMonth.getMonth()]}
                <i className="bi bi-chevron-down ms-1" />
              </button>
              {showMonthMenu ? (
                <div className="promocion-calendario-month-menu">
                  {MONTH_LABELS.map((label, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`promocion-calendario-month-option ${idx === activeMonth.getMonth() ? "is-active" : ""}`}
                      onClick={() => handleMonthPick(idx)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <input
              type="number"
              className="promocion-calendario-year-input"
              value={yearInputValue}
              onChange={handleYearChange}
              onBlur={handleYearBlur}
              onKeyDown={handleYearKeyDown}
              min={1900}
              max={2100}
            />
            {onOpenFeriados ? (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm promocion-calendario-feriados-btn"
                onClick={onOpenFeriados}
                title="Agregar, editar o quitar feriados y días no laborables"
              >
                <i className="bi bi-calendar2-week me-1" />
                Feriados
              </button>
            ) : null}
          </div>
          <div className="promocion-calendario-range">
            <span>Inicio: {toDisplayDate(rangeStart)}</span>
            <span>Fin: {toDisplayDate(rangeEnd)}</span>
          </div>
        </div>
        <div className="promocion-calendario-actions">
          <button type="button" className="btn btn-link p-0" onClick={handleNext}>
            <i className="bi bi-chevron-right" />
          </button>
          {onClose ? (
            <button type="button" className="btn btn-link p-0" onClick={onClose}>
              <i className="bi bi-x-lg" />
            </button>
          ) : null}
        </div>
      </div>
      <div className="promocion-calendario-weekdays">
        {WEEK_LABELS.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <div className="promocion-calendario-grid">
        {weeks.flat().map(({ date, inMonth }) => {
          const key = toDateKey(date);
          const isStart = key === rangeStart;
          const isEnd = key === rangeEnd;
          const isHoliday = nonLaborableSet.has(key);
          const isAllowed = !allowedWeekdays || allowedWeekdays.has(date.getDay());
          const isOffday = Boolean(allowedWeekdays) && !isAllowed;
          // El rango pintado cubre toda sesion real MAS cualquier dia
          // habil (segun el horario) entre inicio y fin declarados, aunque
          // ahi no se haya generado clase: si la coordinacion extendio la
          // fecha de fin a mano para cumplir un minimo de dias, esos dias
          // de margen son decision de ellos, no del sistema.
          const inRange = sessionSet.has(key) || (isInRange(key) && !isOffday && !isHoliday);
          const disabled = !allowSelection || isHoliday || isOffday;
          const classes = [
            "promocion-calendario-day",
            inMonth ? "is-current" : "is-outside",
            inRange ? "is-range" : "",
            isStart ? "is-start" : "",
            isEnd ? "is-end" : "",
            isHoliday ? "is-holiday" : "",
            isOffday ? "is-offday" : "",
            disabled ? "is-disabled" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <button
              key={`${key}-${inMonth ? "in" : "out"}`}
              type="button"
              className={classes}
              onClick={() => {
                if (disabled) return;
                onSelectDate?.(key);
              }}
              disabled={disabled}
              aria-label={key}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
      <div className="promocion-calendario-legend">
        <span className="legend-item">
          <span className="legend-dot is-range" />
          Rango de clases
        </span>
        <span className="legend-item">
          <span className="legend-dot is-holiday" />
          Feriado
        </span>
        <span className="legend-item">
          <span className="legend-dot is-offday" />
          No laborable (sin horario)
        </span>
      </div>
    </div>
  );
};

const FormFields = ({
  form,
  setForm,
  ciclos,
  ciclosLoading,
  ciclosError,
  reloadCiclos,
  cursos,
  cursosLoading = false,
  cursosError,
  reloadCursos,
  horarios = [],
  horariosLoading = false,
  horariosError = "",
  preview = { fecha_fin: "", sesiones: [], no_laborables: [] },
  previewLoading = false,
  previewError = "",
  numeroPromocionLoading = false,
  numeroPromocionError = "",
  reloadNumeroPromocion = () => {},
  lockNumero = false,
  showNumeroReload = false,
  estadoOptions,
  isEdit = false,
  lockEstado = false,
  canUpdatePromocion = true,
  savingEstado = false,
  onToggleCancel,
}) => {
  const isProgramado = (form.estado || "").toLowerCase() === "programado";
  const allowDescuentoEdit = !isEdit || isProgramado;
  const estadoDisabled = Boolean(lockEstado);
  const estadoActual = (form.estado || "").toLowerCase();
  const isCancelado = estadoActual === "cancelado";
  const canToggleCancel = isEdit && lockEstado;
  const canToggle = canUpdatePromocion && !savingEstado;
  const selectedHorario = useMemo(
    () => horarios.find((item) => String(item.id) === String(form.horario_id)),
    [horarios, form.horario_id]
  );
  const allowedWeekdays = useMemo(() => {
    if (!selectedHorario?.dias_semana) return null;
    const set = parseDiasSemanaSet(selectedHorario.dias_semana);
    return set.size ? set : null;
  }, [selectedHorario]);
  const [activeMonth, setActiveMonth] = useState(
    () => parseInputDate(form.fecha_inicio) || new Date()
  );
  const allowSelection = Boolean(form.horario_id);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showFeriadosModal, setShowFeriadosModal] = useState(false);
  const [feriadosNonce, setFeriadosNonce] = useState(0);
  const [calendarNoLaborables, setCalendarNoLaborables] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState("");
  const [codigoMDI, setCodigoMDI] = useState("");
  const [codigoLoading, setCodigoLoading] = useState(false);
  const [camposVacios, setCamposVacios] = useState([]);
  const codigoKeyRef = useRef("");
  const fechaFinNativeRef = useRef(null);
  // Solo informativo (no bloquea nada): así el coordinador ve de un vistazo
  // cuántos días de calendario abarca lo seleccionado y decide si ajustar la
  // fecha de fin, sin que el sistema tenga que adivinar si el requisito del
  // Ministerio es días calendario o días de clase reales.
  const diasSeleccionados = useMemo(
    () => contarDiasCalendario(form.fecha_inicio, form.fecha_fin),
    [form.fecha_inicio, form.fecha_fin]
  );

  // Preview del código MDI cuando ciclo + curso + numero_promocion están completos
  useEffect(() => {
    const cicloId = form.ciclo_id;
    const cursoId = form.curso_id;
    const numero = form.numero_promocion;
    if (!cicloId || !cursoId || !numero) {
      codigoKeyRef.current = "";
      setCodigoMDI("");
      setCamposVacios([]);
      return;
    }
    const key = `${cicloId}-${cursoId}-${numero}`;
    if (codigoKeyRef.current === key) return;
    codigoKeyRef.current = key;
    setCodigoLoading(true);
    previsualizarCodigo({ cicloId, cursoId, numeroPromocion: numero })
      .then((data) => {
        if (codigoKeyRef.current !== key) return;
        setCodigoMDI(data?.codigo || "");
        setCamposVacios(Array.isArray(data?.campos_vacios) ? data.campos_vacios : []);
      })
      .catch(() => {
        if (codigoKeyRef.current !== key) return;
        setCodigoMDI("");
        setCamposVacios([]);
      })
      .finally(() => {
        if (codigoKeyRef.current === key) setCodigoLoading(false);
      });
  }, [form.ciclo_id, form.curso_id, form.numero_promocion]);
  const calendarKeyRef = useRef("");
  const mergedNoLaborables = useMemo(() => {
    const set = new Set();
    (preview?.no_laborables || []).forEach((item) => set.add(String(item)));
    (calendarNoLaborables || []).forEach((item) => set.add(String(item)));
    return Array.from(set);
  }, [preview?.no_laborables, calendarNoLaborables]);
  const handleEstadoChange = (nextEstado) => {
    if (onToggleCancel) {
      onToggleCancel(nextEstado);
      return;
    }
    setForm((prev) => ({ ...prev, estado: nextEstado }));
  };

  useEffect(() => {
    const parsed = parseInputDate(form.fecha_inicio);
    if (parsed) {
      setActiveMonth(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
    }
  }, [form.fecha_inicio]);

  useEffect(() => {
    if (!showCalendar) return;
    if (!form.curso_id) {
      calendarKeyRef.current = "";
      setCalendarNoLaborables([]);
      setCalendarError("");
      setCalendarLoading(false);
      return;
    }

    const weeks = buildCalendarWeeks(activeMonth.getFullYear(), activeMonth.getMonth());
    const days = weeks.flat().map((item) => item.date);
    if (!days.length) return;
    const desde = toDateKey(days[0]);
    const hasta = toDateKey(days[days.length - 1]);
    const key = `${form.curso_id}-${desde}-${hasta}-${feriadosNonce}`;
    if (calendarKeyRef.current === key) return;

    calendarKeyRef.current = key;
    setCalendarLoading(true);
    setCalendarError("");

    listarNoLaborablesPromocion({ cursoId: form.curso_id, desde, hasta })
      .then((data) => {
        if (calendarKeyRef.current !== key) return;
        const list = Array.isArray(data?.no_laborables)
          ? data.no_laborables
          : Array.isArray(data)
            ? data
            : [];
        setCalendarNoLaborables(list);
      })
      .catch((err) => {
        if (calendarKeyRef.current !== key) return;
        console.error(err);
        setCalendarNoLaborables([]);
        setCalendarError(err.message || "No se pudieron cargar los feriados.");
        calendarKeyRef.current = "";
      })
      .finally(() => {
        if (calendarKeyRef.current === key) {
          setCalendarLoading(false);
        }
      });
  }, [showCalendar, form.curso_id, activeMonth, feriadosNonce]);

  const toggleCalendar = () => setShowCalendar((prev) => !prev);
  const openCalendar = () => setShowCalendar(true);

  return (
    <>
    <div className="row g-3">
      <div className="col-md-5">
        <div className="d-flex justify-content-between align-items-center">
          <label className="form-label fw-semibold mb-0">Ciclo</label>
          <button
            type="button"
            className="btn btn-link btn-sm text-decoration-none p-0"
            onClick={reloadCiclos}
          >
            <i className="bi bi-arrow-repeat me-1" />
            Recargar
          </button>
        </div>
        <select
          className="form-select"
          value={form.ciclo_id}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, ciclo_id: e.target.value, curso_id: "" }))
          }
          disabled={ciclosLoading}
          required
        >
          {renderCicloOptions(ciclos, ciclosLoading)}
        </select>
        {ciclosError ? (
          <div className="form-text text-danger small">{ciclosError}</div>
        ) : null}
      </div>
      <div className="col-md-5">
        <div className="d-flex justify-content-between align-items-center">
          <label className="form-label fw-semibold mb-0">Curso</label>
          <button
            type="button"
            className="btn btn-link btn-sm text-decoration-none p-0"
            onClick={reloadCursos}
          >
            <i className="bi bi-arrow-repeat me-1" />
            Recargar
          </button>
        </div>
        <select
          className="form-select"
          value={form.curso_id}
          onChange={(e) => setForm((prev) => ({ ...prev, curso_id: e.target.value }))}
          disabled={cursosLoading || !form.ciclo_id}
          required
        >
          {renderCursoOptions(cursos, cursosLoading, form.ciclo_id)}
        </select>
        {cursosError ? (
          <div className="form-text text-danger small">{cursosError}</div>
        ) : null}
      </div>
      <div className="col-md-2">
        <div className="d-flex justify-content-between align-items-center">
          <label className="form-label fw-semibold mb-0">Nro</label>
          {showNumeroReload ? (
            <button
              type="button"
              className="btn btn-link btn-sm text-decoration-none p-0"
              onClick={reloadNumeroPromocion}
              disabled={numeroPromocionLoading || !form.ciclo_id || !form.curso_id}
              title="Recargar numero"
            >
              <i className="bi bi-arrow-repeat" />
            </button>
          ) : null}
        </div>
        <input
          type="text"
          className="form-control form-control-sm"
          value={form.numero_promocion}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, numero_promocion: e.target.value }))
          }
          placeholder={numeroPromocionLoading ? "Calculando..." : "Ej: 0276"}
          disabled={!form.ciclo_id || !form.curso_id || numeroPromocionLoading}
          required
        />
        {numeroPromocionError ? (
          <div className="form-text text-danger small">{numeroPromocionError}</div>
        ) : null}
      </div>
    </div>
    {(codigoMDI || codigoLoading) && (
      <div className="row g-3 mt-0">
        <div className="col-12">
          <label className="form-label fw-semibold mb-0 text-muted small">Codigo de convocatoria generado</label>
          <div className="input-group input-group-sm">
            <span className="input-group-text"><i className="bi bi-tag" /></span>
            <input
              type="text"
              className="form-control form-control-sm font-monospace"
              value={codigoLoading ? "Calculando..." : (codigoMDI || "—")}
              readOnly
            />
          </div>
          {camposVacios.length > 0 ? (
            <div className="form-text text-warning">
              <i className="bi bi-exclamation-triangle me-1" />
              Codigo incompleto. Para verlo completo, llena en el sistema:{" "}
              <strong>{camposVacios.join(", ")}</strong>.
            </div>
          ) : (
            <div className="form-text text-muted">Generado automaticamente segun la plantilla del ciclo.</div>
          )}
        </div>
      </div>
    )}
    <div className="row g-3">
      <div className="col-12 col-md-3">
        <label className="form-label fw-semibold">Horario</label>
        <select
          className="form-select"
          value={form.horario_id || ""}
          onChange={(e) => setForm((prev) => ({ ...prev, horario_id: e.target.value }))}
          disabled={horariosLoading || !form.curso_id}
          required
        >
          {renderHorarioOptions(horarios, horariosLoading, form.curso_id)}
        </select>
        {horariosError ? (
          <div className="form-text text-danger small">{horariosError}</div>
        ) : null}
      </div>
      <div className="col-12 col-md-3">
        <label className="form-label fw-semibold">Fecha de inicio</label>
        <input
          type="text"
          className="form-control"
          value={form.fecha_inicio ? toDisplayDate(form.fecha_inicio) : ""}
          readOnly
          placeholder="dd/mm/aaaa"
          onClick={openCalendar}
          onFocus={openCalendar}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openCalendar();
            }
          }}
        />
      </div>
      <div className="col-12 col-md-3">
        <label className="form-label fw-semibold">Fecha de fin</label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            value={form.fecha_fin ? toDisplayDate(form.fecha_fin) : ""}
            readOnly
            placeholder="dd/mm/aaaa"
            onClick={() => fechaFinNativeRef.current?.showPicker?.()}
          />
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => fechaFinNativeRef.current?.showPicker?.()}
            title="Elegir fecha de fin"
          >
            <i className="bi bi-calendar2-check" />
          </button>
          <input
            ref={fechaFinNativeRef}
            type="date"
            value={form.fecha_fin || ""}
            min={preview?.fecha_fin || undefined}
            onChange={(e) => setForm((prev) => ({ ...prev, fecha_fin: e.target.value }))}
            style={{ width: 0, height: 0, padding: 0, border: 0, opacity: 0, pointerEvents: "none" }}
            tabIndex={-1}
            aria-hidden="true"
          />
        </div>
      </div>
      <div className="col-12 col-md-3">
        <label className="form-label fw-semibold">Duración</label>
        <div className="form-control bg-body-secondary text-center fw-semibold">
          {diasSeleccionados != null ? `${diasSeleccionados} día${diasSeleccionados === 1 ? "" : "s"}` : "—"}
        </div>
      </div>
    </div>
    <CalendarioNoLaborableCalendarModal
      open={showFeriadosModal}
      initialDate={form.fecha_inicio}
      onClose={(changed) => {
        setShowFeriadosModal(false);
        if (changed) setFeriadosNonce((n) => n + 1);
      }}
    />
    <div className="row g-3 mt-0">
      <div className="col-12">
        {showCalendar ? (
          <PromocionCalendarioRange
            startDate={form.fecha_inicio}
            endDate={form.fecha_fin}
            onSelectDate={(value) => {
              setForm((prev) => ({ ...prev, fecha_inicio: value }));
              setShowCalendar(false);
            }}
            activeMonth={activeMonth}
            setActiveMonth={setActiveMonth}
            nonLaborables={mergedNoLaborables}
            allowedWeekdays={allowedWeekdays}
            allowSelection={allowSelection}
            sessionDates={preview?.sesiones}
            onClose={toggleCalendar}
            onOpenFeriados={() => setShowFeriadosModal(true)}
            loading={previewLoading || calendarLoading}
          />
        ) : null}
        {!allowSelection ? (
          <div className="form-text text-muted mt-2">Selecciona un horario para habilitar el calendario.</div>
        ) : null}
        {previewLoading ? (
          <div className="form-text text-muted mt-2">Calculando rango...</div>
        ) : null}
        {calendarError ? (
          <div className="form-text text-danger small mt-2">{calendarError}</div>
        ) : null}
        {previewError ? (
          <div className="form-text text-danger small mt-2">{previewError}</div>
        ) : null}
      </div>
    </div>
    <div className="row g-3 mt-0">
      <div className="col-md-4">
        <label className="form-label fw-semibold">Capacidad</label>
        <input
          type="number"
          min="1"
          className="form-control"
          value={form.capacidad ?? ""}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              capacidad: e.target.value ? Number(e.target.value) : "",
            }))
          }
        />
      </div>
      <div className="col-md-4">
        <label className="form-label fw-semibold">Estado</label>
        <select
          className="form-select"
          value={form.estado}
          onChange={(e) => setForm((prev) => ({ ...prev, estado: e.target.value }))}
          disabled={estadoDisabled}
        >
          {estadoOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {estadoDisabled && (
          <div className="form-text text-muted">
           {/*  El estado se calcula automaticamente segun las fechas. */}
          </div>
        )}
        {canToggleCancel && (
          <button
            type="button"
            className={`btn btn-sm mt-2 ${isCancelado ? "btn-outline-secondary" : "btn-outline-danger"}`}
            onClick={() => {
              if (isCancelado) {
                if (!window.confirm("Deseas reactivar la promocion?")) return;
                handleEstadoChange("programado");
                return;
              }
              if (!window.confirm("Deseas cancelar la promocion?")) return;
              handleEstadoChange("cancelado");
            }}
            disabled={!canToggle}
          >
            {isCancelado ? "Reactivar promocion" : "Cancelar promocion"}
          </button>
        )}
      </div>
      <div className="col-md-4">
        <label className="form-label fw-semibold">Descuento (%)</label>
        <input
          type="number"
          min="0"
          step="0.01"
          className="form-control"
          value={form.descuento_porcentaje ?? ""}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              descuento_porcentaje: e.target.value,
            }))
          }
          disabled={!allowDescuentoEdit}
          required
        />
        {!allowDescuentoEdit && (
          <div className="form-text text-muted">
           {/*  El descuento solo se puede editar cuando la promocion esta en estado Programado. */}
          </div>
        )}
      </div>
    </div>
    <div className="mb-3 mt-0">
      <label className="form-label fw-semibold">Link de WhatsApp (opcional)</label>
      <input
        type="url"
        className="form-control"
        placeholder="https://chat.whatsapp.com/..."
        value={form.link_whatsapp}
        onChange={(e) => setForm((prev) => ({ ...prev, link_whatsapp: e.target.value }))}
      />
    </div>
    <div className="form-check form-switch">
      <input
        className="form-check-input"
        type="checkbox"
        id="promocion-activa"
        checked={form.activo}
        onChange={(e) => setForm((prev) => ({ ...prev, activo: e.target.checked }))}
      />
      <label className="form-check-label" htmlFor="promocion-activa">
        {form.activo ? "Promocion activa" : "Promocion inactiva"}
      </label>
    </div>
    </>
  );
};

export default function PromocionFormModal({
  modal,
  cerrarModal,
  formNuevo,
  setFormNuevo,
  formEditar,
  setFormEditar,
  ciclos = [],
  ciclosLoading = false,
  ciclosError = null,
  reloadCiclos = () => {},
  cursosNuevo = [],
  cursosEditar = [],
  cursosLoadingNuevo = false,
  cursosLoadingEditar = false,
  cursosError,
  reloadCursos,
  horariosNuevo = [],
  horariosEditar = [],
  horariosLoadingNuevo = false,
  horariosLoadingEditar = false,
  horariosErrorNuevo = "",
  horariosErrorEditar = "",
  previewNuevo = { fecha_fin: "", sesiones: [], no_laborables: [] },
  previewEditar = { fecha_fin: "", sesiones: [], no_laborables: [] },
  previewLoadingNuevo = false,
  previewLoadingEditar = false,
  previewErrorNuevo = "",
  previewErrorEditar = "",
  numeroPromocionLoading = false,
  numeroPromocionError = "",
  reloadNumeroPromocion = () => {},
  estadoOptions,
  savingNuevo,
  savingEditar,
  deleting,
  detalleLoading,
  canCreatePromocion,
  canUpdatePromocion,
  canDeletePromocion,
  promocionSeleccionada,
  handleCrearPromocion,
  handleActualizarPromocion,
  handleEliminarPromocion,
  onToggleCancel,
}) {
  return (
    <>
      <PromocionesModal
        isOpen={modal === "nuevo"}
        size="modal-md"
        title="Registrar promocion"
        onClose={cerrarModal}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleCrearPromocion(e);
          }}
        >
          <div className="modal-body pt-0">
            <FormFields
              form={formNuevo}
              setForm={setFormNuevo}
              ciclos={ciclos}
              ciclosLoading={ciclosLoading}
              ciclosError={ciclosError}
              reloadCiclos={reloadCiclos}
              cursos={cursosNuevo}
              cursosLoading={cursosLoadingNuevo}
              cursosError={cursosError}
              reloadCursos={reloadCursos}
              horarios={horariosNuevo}
              horariosLoading={horariosLoadingNuevo}
              horariosError={horariosErrorNuevo}
              preview={previewNuevo}
              previewLoading={previewLoadingNuevo}
              previewError={previewErrorNuevo}
              numeroPromocionLoading={numeroPromocionLoading}
              numeroPromocionError={numeroPromocionError}
              reloadNumeroPromocion={reloadNumeroPromocion}
              showNumeroReload
              estadoOptions={estadoOptions}
              lockEstado
            />
          </div>
          <div className="modal-footer border-0 pt-0">
            <button type="button" className="btn btn-outline-secondary" onClick={cerrarModal}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn management-btn-primary"
              disabled={savingNuevo || !canCreatePromocion}
            >
              {savingNuevo ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Guardando...
                </>
              ) : (
                "Guardar"
              )}
            </button>
          </div>
        </form>
      </PromocionesModal>

      <PromocionesModal
        isOpen={modal === "editar"}
        size="modal-md"
        title="Editar promocion"
        onClose={cerrarModal}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleActualizarPromocion(e);
          }}
        >
          <div className="modal-body pt-0">
            {detalleLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
              </div>
            ) : (
              <>
                <FormFields
                  form={formEditar}
                  setForm={setFormEditar}
                  ciclos={ciclos}
                  ciclosLoading={ciclosLoading}
                  ciclosError={ciclosError}
                  reloadCiclos={reloadCiclos}
                  cursos={cursosEditar}
                  cursosLoading={cursosLoadingEditar}
                  cursosError={cursosError}
                  reloadCursos={reloadCursos}
                  horarios={horariosEditar}
                  horariosLoading={horariosLoadingEditar}
                  horariosError={horariosErrorEditar}
                  preview={previewEditar}
                  previewLoading={previewLoadingEditar}
                  previewError={previewErrorEditar}
                  estadoOptions={estadoOptions}
                  isEdit
                  lockEstado
                  canUpdatePromocion={canUpdatePromocion}
                  savingEstado={savingEditar}
                  onToggleCancel={onToggleCancel}
                />
              </>
            )}
          </div>
          <div className="modal-footer border-0 pt-0">
            <button type="button" className="btn btn-outline-secondary" onClick={cerrarModal}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn management-btn-primary"
              disabled={savingEditar || detalleLoading || !canUpdatePromocion}
            >
              {savingEditar ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Guardando...
                </>
              ) : (
                "Guardar cambios"
              )}
            </button>
          </div>
        </form>
      </PromocionesModal>

      <PromocionesModal
        isOpen={modal === "eliminar" && Boolean(promocionSeleccionada)}
        size="modal-md"
        title="Eliminar promocion"
        onClose={cerrarModal}
      >
        <div className="modal-body pt-0">
          <p className="mb-2">
            Estas a punto de eliminar la promocion <strong>{promocionSeleccionada?.nombre}</strong>.
          </p>
          <p className="text-muted mb-0">
            Esta accion no borrara historicos, pero evitara nuevas matriculas en ella.
          </p>
        </div>
        <div className="modal-footer border-0 pt-0">
          <button type="button" className="btn btn-outline-secondary" onClick={cerrarModal}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleEliminarPromocion}
            disabled={deleting || !canDeletePromocion}
          >
            {deleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Eliminando...
              </>
            ) : (
              "Eliminar"
            )}
          </button>
        </div>
      </PromocionesModal>
    </>
  );
}
